import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../../../../shared/lib/supabase'
import { useAuth } from '../../../../../contexts/AuthContext'
import { useTechnologyStack } from '../../../../assessment/hooks/useTechnologyStack'
import MaesterReportUpload from '../../../../../shared/components/MaesterReportUpload'
import cdmData from '../../../../../shared/data/cyberDefenseMatrix.json'
import { useAIJobs } from '../../../../../contexts/AIJobsContext'

const TIER_ORDER = { critical: 0, high: 1, moderate: 2, low: 3 }

const TIER_STYLES = {
  critical: {
    badge: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    card: 'bg-red-50 dark:bg-red-900/20',
    count: 'text-red-600 dark:text-red-400',
    label: 'text-red-600 dark:text-red-400',
    radio: 'text-red-600'
  },
  high: {
    badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    card: 'bg-yellow-50 dark:bg-yellow-900/20',
    count: 'text-yellow-600 dark:text-yellow-400',
    label: 'text-yellow-600 dark:text-yellow-400',
    radio: 'text-yellow-600'
  },
  moderate: {
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    card: 'bg-blue-50 dark:bg-blue-900/20',
    count: 'text-blue-600 dark:text-blue-400',
    label: 'text-blue-600 dark:text-blue-400',
    radio: 'text-blue-600'
  },
  low: {
    badge: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    card: 'bg-gray-50 dark:bg-gray-700/50',
    count: 'text-gray-600 dark:text-gray-400',
    label: 'text-gray-600 dark:text-gray-400',
    radio: 'text-gray-600'
  }
}

const ASSET_TYPES = [
  'Hardware (On-Prem)', 'Software (COTS/Internal)', 'Cloud & Infrastructure',
  'SaaS', 'Data Stores', 'Credentials'
]

const BUSINESS_FUNCTIONS = [
  'Patient Care / Clinical', 'Revenue / Billing', 'Operations / CRM',
  'Communications', 'HR / Payroll', 'Finance / Accounting',
  'Legal / Contracts', 'Manufacturing / Production', 'Supply Chain / Logistics',
  'Marketing / Sales', 'R&D', 'Compliance / Regulatory',
  'Customer Service', 'Other'
]

const DATA_SENSITIVITY_OPTIONS = [
  'PHI', 'PII', 'Financial', 'Intellectual Property',
  'Legal / Contractual', 'Proprietary / Trade Secret', 'Public'
]

const TIER_DESCRIPTIONS = {
  critical: 'System failure causes immediate revenue loss, regulatory violation, or safety risk',
  high: 'Failure significantly disrupts operations within hours',
  moderate: 'Failure causes inconvenience, workarounds available',
  low: 'Failure has minimal business impact'
}

const emptyForm = {
  name: '', asset_type: '', is_third_party: false, vendor: '',
  business_function: '', criticality_tier: 'moderate',
  data_sensitivity: [], business_impact: '', owner: '', notes: '',
  rto_hours: '', rpo_hours: '', mtd_hours: '', hourly_cost_impact: '',
  revenue_dependency_pct: '', ai_estimated: false, ai_reasoning: ''
}

function formatHours(h) {
  if (h == null || h === '') return '—'
  const n = Number(h)
  if (n < 1) return `${Math.round(n * 60)}min`
  if (n >= 24) return `${Math.round(n / 24)}d`
  return `${n}h`
}

function formatCost(v) {
  if (v == null || v === '') return '—'
  const n = Number(v)
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`
  if (n >= 1000) return `$${Math.round(n / 1000)}K`
  return `$${Math.round(n)}`
}

function SecurityPostureTab({ client, defaultView, onNavigate }) {
  const [section, setSection] = useState(defaultView || 'assets')
  const { isAdmin } = useAuth()

  // Sync section when defaultView changes (navigating between nav items)
  useEffect(() => {
    if (defaultView) setSection(defaultView)
  }, [defaultView])
  const { technologies, buildCdmGrid } = useTechnologyStack(client.id)
  const cdmGrid = buildCdmGrid()

  // Critical systems state
  const [systems, setSystems] = useState([])
  const [csLoading, setCsLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingSystem, setEditingSystem] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null)
  const [biaError, setBiaError] = useState(null)
  const [biaJobKey, setBiaJobKey] = useState(null)
  const { startJob, getJob } = useAIJobs()
  const biaJob = biaJobKey ? getJob(biaJobKey) : null
  const estimatingBia = biaJob?.status === 'running'
  const lastAppliedBiaKeyRef = useRef(null)

  // Fetch critical systems
  useEffect(() => {
    if (!client.id) return
    const fetchSystems = async () => {
      setCsLoading(true)
      const { data } = await supabase
        .from('client_critical_systems')
        .select('*')
        .eq('client_id', client.id)
      setSystems(data || [])
      setCsLoading(false)
    }
    fetchSystems()
  }, [client.id])

  const sorted = [...systems].sort((a, b) =>
    (TIER_ORDER[a.criticality_tier] ?? 2) - (TIER_ORDER[b.criticality_tier] ?? 2)
  )

  const tierCounts = {
    critical: systems.filter(s => s.criticality_tier === 'critical').length,
    high: systems.filter(s => s.criticality_tier === 'high').length,
    moderate: systems.filter(s => s.criticality_tier === 'moderate').length,
    low: systems.filter(s => s.criticality_tier === 'low').length
  }

  const openAddModal = () => {
    setEditingSystem(null)
    setForm(emptyForm)
    setBiaError(null)
    setShowModal(true)
  }

  const openEditModal = (system) => {
    setEditingSystem(system)
    setForm({
      name: system.name || '',
      asset_type: system.asset_type || '',
      // Legacy rows predate the flag: treat any row with a vendor as third-party.
      is_third_party: system.is_third_party ?? !!(system.vendor && system.vendor.trim()),
      vendor: system.vendor || '',
      business_function: system.business_function || '',
      criticality_tier: system.criticality_tier || 'moderate',
      data_sensitivity: system.data_sensitivity || [],
      business_impact: system.business_impact || '',
      owner: system.owner || '',
      notes: system.notes || '',
      rto_hours: system.rto_hours != null ? String(system.rto_hours) : '',
      rpo_hours: system.rpo_hours != null ? String(system.rpo_hours) : '',
      mtd_hours: system.mtd_hours != null ? String(system.mtd_hours) : '',
      hourly_cost_impact: system.hourly_cost_impact != null ? String(system.hourly_cost_impact) : '',
      revenue_dependency_pct: system.revenue_dependency_pct != null ? String(system.revenue_dependency_pct) : '',
      ai_estimated: system.ai_estimated || false,
      ai_reasoning: system.ai_reasoning || ''
    })
    setShowModal(true)
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      const payload = {
        client_id: client.id,
        name: form.name,
        asset_type: form.asset_type,
        is_third_party: form.is_third_party,
        // Vendor name only persists when the asset is flagged third-party.
        vendor: form.is_third_party ? form.vendor : '',
        business_function: form.business_function,
        criticality_tier: form.criticality_tier,
        data_sensitivity: form.data_sensitivity,
        business_impact: form.business_impact,
        owner: form.owner,
        notes: form.notes,
        rto_hours: form.rto_hours !== '' ? parseFloat(form.rto_hours) : null,
        rpo_hours: form.rpo_hours !== '' ? parseFloat(form.rpo_hours) : null,
        mtd_hours: form.mtd_hours !== '' ? parseFloat(form.mtd_hours) : null,
        hourly_cost_impact: form.hourly_cost_impact !== '' ? parseFloat(form.hourly_cost_impact) : null,
        revenue_dependency_pct: form.revenue_dependency_pct !== '' ? parseFloat(form.revenue_dependency_pct) : null,
        // Clear AI flag if user manually edited BIA fields
        ai_estimated: editingSystem?.ai_estimated && form.rto_hours === String(editingSystem.rto_hours) && form.hourly_cost_impact === String(editingSystem.hourly_cost_impact) ? true : false,
        ai_reasoning: form.ai_reasoning
      }

      if (editingSystem) {
        const { data } = await supabase
          .from('client_critical_systems')
          .update(payload)
          .eq('id', editingSystem.id)
          .select()
          .single()
        setSystems(prev => prev.map(s => s.id === editingSystem.id ? data : s))
      } else {
        const { data } = await supabase
          .from('client_critical_systems')
          .insert(payload)
          .select()
          .single()
        setSystems(prev => [...prev, data])
      }
      setShowModal(false)
    } catch (err) {
      console.error('Error saving critical system:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    try {
      await supabase.from('client_critical_systems').delete().eq('id', id)
      setSystems(prev => prev.filter(s => s.id !== id))
      setShowDeleteConfirm(null)
    } catch (err) {
      console.error('Error deleting critical system:', err)
    }
  }

  const toggleSensitivity = (val) => {
    setForm(prev => ({
      ...prev,
      data_sensitivity: prev.data_sensitivity.includes(val)
        ? prev.data_sensitivity.filter(v => v !== val)
        : [...prev.data_sensitivity, val]
    }))
  }

  const handleGenerateBia = () => {
    if (!form.name?.trim()) return
    setBiaError(null)

    const key = `bia-estimate:${client.id}:${form.name.trim().toLowerCase()}:${(form.vendor || '').toLowerCase()}:${form.criticality_tier || ''}:${(form.data_sensitivity || []).join(',')}`
    lastAppliedBiaKeyRef.current = null
    setBiaJobKey(key)

    const payload = {
      systems: [{
        name: form.name,
        vendor: form.vendor,
        businessFunction: form.business_function,
        criticalityTier: form.criticality_tier,
        dataSensitivity: form.data_sensitivity
      }],
      industry: client.industry,
      subCategory: client.industry_sub_category || client.industrySubCategory,
      annualRevenue: client.annual_revenue || client.annualRevenue,
      employeeCount: client.staffing_size || client.staffingSize
    }

    startJob(key, async () => {
      const res = await fetch('/api/ai/bia-estimate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || 'AI estimation failed')
      }
      return await res.json()
    }).catch(err => {
      console.error('[BIA Estimate] Error:', err)
    })
  }

  // Apply BIA result when it lands
  useEffect(() => {
    if (!biaJobKey || biaJob?.status !== 'success' || lastAppliedBiaKeyRef.current === biaJobKey) return
    lastAppliedBiaKeyRef.current = biaJobKey
    const est = biaJob.result?.estimates?.[0]
    if (!est) return
    setForm(prev => ({
      ...prev,
      rto_hours: String(est.rtoHours),
      rpo_hours: String(est.rpoHours),
      mtd_hours: String(est.mtdHours),
      hourly_cost_impact: String(est.hourlyCostImpact),
      revenue_dependency_pct: String(est.revenueDependencyPct),
      ai_estimated: true,
      ai_reasoning: (est.reasoning || '').replace(/—/g, ', ').replace(/–/g, ', ')
    }))
  }, [biaJobKey, biaJob?.status, biaJob?.result])

  // Surface BIA errors
  useEffect(() => {
    if (biaJob?.status === 'error') setBiaError(biaJob.error)
  }, [biaJob?.status, biaJob?.error])

  // When defaultView is set, render directly without pill selector
  const directView = defaultView === 'assets' || defaultView === 'techstack' || defaultView === 'm365'

  return (
    <div className="space-y-6">
      {/* Sub-toggle (pills) — only show when no direct view is specified */}
      {!directView && (
        <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
          <button
            onClick={() => setSection('assets')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-all ${
              section === 'assets'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Assets
          </button>
          <button
            onClick={() => setSection('techstack')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-all ${
              section === 'techstack'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Security Stack
          </button>
          <button
            onClick={() => setSection('m365')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-all ${
              section === 'm365'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            M365 Reports
          </button>
        </div>
      )}

      {section === 'm365' ? (
        <MaesterReportUpload clientId={client.id} />
      ) : section === 'assets' ? (
        <>
          {/* Tier summary cards */}
          <div className="grid grid-cols-4 gap-3">
            {['critical', 'high', 'moderate', 'low'].map(tier => (
              <div key={tier} className={`${TIER_STYLES[tier].card} rounded-lg p-4 text-center`}>
                <p className={`text-xs font-semibold uppercase tracking-wider mb-1 ${TIER_STYLES[tier].label}`}>
                  {tier.charAt(0).toUpperCase() + tier.slice(1)}
                </p>
                <span className={`text-xl font-bold ${TIER_STYLES[tier].count}`}>
                  {tierCounts[tier]}
                </span>
              </div>
            ))}
          </div>

          {/* Header row */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {systems.length} asset{systems.length !== 1 ? 's' : ''} documented
            </p>
            {isAdmin && (
              <button onClick={openAddModal} className="btn-primary text-sm flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                Add Asset
              </button>
            )}
          </div>

          {/* Systems table or empty state */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            {csLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : systems.length === 0 ? (
              <div className="p-12 text-center">
                <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-gray-500 dark:text-gray-400 mb-2">No assets documented yet</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mb-4">
                  Catalog your client's crown jewels, the assets that matter most to their business.
                </p>
                {isAdmin && (
                  <button onClick={openAddModal} className="btn-primary">Add First Asset</button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700/50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Business Function</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Criticality</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Data Sensitivity</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">RTO</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">$/Hour</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Rev. Dep.</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Owner</th>
                      {isAdmin && (
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map(system => (
                      <tr
                        key={system.id}
                        onClick={() => isAdmin && openEditModal(system)}
                        className={`border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${isAdmin ? 'cursor-pointer' : ''}`}
                      >
                        <td className="px-4 py-3 text-sm">
                          <div className="font-medium text-gray-900 dark:text-white">{system.name}</div>
                          {system.vendor && <div className="text-xs text-gray-500 dark:text-gray-400">{system.vendor}</div>}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{system.asset_type || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{system.business_function}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${TIER_STYLES[system.criticality_tier]?.badge || TIER_STYLES.moderate.badge}`}>
                            {system.criticality_tier.charAt(0).toUpperCase() + system.criticality_tier.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex flex-wrap gap-1">
                            {(system.data_sensitivity || []).map(ds => (
                              <span key={ds} className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                {ds}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          <span title={system.rto_hours != null ? `RTO: ${system.rto_hours}h / RPO: ${system.rpo_hours != null ? system.rpo_hours + 'h' : '—'}` : ''}>
                            {formatHours(system.rto_hours)}
                          </span>
                          {system.ai_estimated && system.rto_hours != null && (
                            <span className="ml-1 text-[10px] text-amber-500 dark:text-amber-400" title={system.ai_reasoning || 'AI-estimated'}>AI</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{formatCost(system.hourly_cost_impact)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{system.revenue_dependency_pct != null ? `${system.revenue_dependency_pct}%` : '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{system.owner || '—'}</td>
                        {isAdmin && (
                          <td className="px-4 py-3 text-sm" onClick={e => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => openEditModal(system)}
                                className="p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                title="Edit"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => setShowDeleteConfirm(system)}
                                className="p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                title="Delete"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Add/Edit Modal */}
          {showModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    {editingSystem ? 'Edit Asset' : 'Add Asset'}
                  </h2>
                  <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form onSubmit={handleSave} className="space-y-4">
                  {/* Asset Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Asset Name</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="e.g., Epic EHR, QuickBooks, Salesforce"
                      required
                    />
                  </div>

                  {/* Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                    <select
                      value={form.asset_type}
                      onChange={e => setForm(prev => ({ ...prev, asset_type: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select type...</option>
                      {ASSET_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>

                    {/* Third-Party/Vendor toggle + conditional vendor name */}
                    <label className="mt-3 flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={form.is_third_party}
                        onChange={e => setForm(prev => ({ ...prev, is_third_party: e.target.checked }))}
                        className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                      />
                      Third-Party/Vendor
                    </label>
                    {form.is_third_party && (
                      <div className="mt-2">
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Third-Party/Vendor</label>
                        <input
                          type="text"
                          value={form.vendor}
                          onChange={e => setForm(prev => ({ ...prev, vendor: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="e.g., Epic Systems, Intuit, Blackbaud"
                        />
                      </div>
                    )}
                  </div>

                  {/* Business Function */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Business Function</label>
                    <select
                      value={form.business_function}
                      onChange={e => setForm(prev => ({ ...prev, business_function: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select function...</option>
                      {BUSINESS_FUNCTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>

                  {/* Criticality Tier */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Criticality Tier</label>
                    <div className="space-y-2">
                      {['critical', 'high', 'moderate', 'low'].map(tier => (
                        <label key={tier} className="flex items-start gap-3 cursor-pointer p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/30">
                          <input
                            type="radio"
                            name="criticality_tier"
                            value={tier}
                            checked={form.criticality_tier === tier}
                            onChange={() => setForm(prev => ({ ...prev, criticality_tier: tier }))}
                            className={`mt-0.5 ${TIER_STYLES[tier].radio} focus:ring-primary-500`}
                          />
                          <div>
                            <span className={`text-sm font-medium ${TIER_STYLES[tier].label}`}>
                              {tier.charAt(0).toUpperCase() + tier.slice(1)}
                            </span>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{TIER_DESCRIPTIONS[tier]}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Data Sensitivity */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Data Sensitivity</label>
                    <div className="grid grid-cols-2 gap-2">
                      {DATA_SENSITIVITY_OPTIONS.map(opt => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
                          <input
                            type="checkbox"
                            checked={form.data_sensitivity.includes(opt)}
                            onChange={() => toggleSensitivity(opt)}
                            className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Business Impact */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">What happens if this system goes down?</label>
                    <textarea
                      value={form.business_impact}
                      onChange={e => setForm(prev => ({ ...prev, business_impact: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                      style={{ minHeight: '80px' }}
                    />
                  </div>

                  {/* BIA Metrics */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Impact Analysis
                        {form.ai_estimated && <span className="ml-2 text-xs font-normal text-amber-500 dark:text-amber-400">AI-estimated</span>}
                      </label>
                      {form.name?.trim() && (
                        <button
                          type="button"
                          onClick={handleGenerateBia}
                          disabled={estimatingBia}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-md hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors disabled:opacity-50"
                        >
                          {estimatingBia ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-600" />
                              Estimating...
                            </>
                          ) : (
                            <>
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                              </svg>
                              Generate BIA Estimates
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    {biaError && (
                      <p className="text-xs text-red-600 dark:text-red-400 mb-2">{biaError}</p>
                    )}
                    {form.ai_reasoning && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 italic">{form.ai_reasoning}</p>
                    )}
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">RTO (hours)</label>
                        <input
                          type="number" step="0.25" min="0"
                          value={form.rto_hours}
                          onChange={e => setForm(prev => ({ ...prev, rto_hours: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          placeholder="4"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">RPO (hours)</label>
                        <input
                          type="number" step="0.25" min="0"
                          value={form.rpo_hours}
                          onChange={e => setForm(prev => ({ ...prev, rpo_hours: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          placeholder="1"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">MTD (hours)</label>
                        <input
                          type="number" step="1" min="0"
                          value={form.mtd_hours}
                          onChange={e => setForm(prev => ({ ...prev, mtd_hours: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          placeholder="24"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Hourly Cost Impact</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                          <input
                            type="text" inputMode="numeric"
                            value={form.hourly_cost_impact ? Number(form.hourly_cost_impact).toLocaleString() : ''}
                            onChange={e => {
                              const raw = e.target.value.replace(/[^0-9]/g, '')
                              setForm(prev => ({ ...prev, hourly_cost_impact: raw }))
                            }}
                            className="w-full pl-7 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                            placeholder="5,000"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Revenue Dependency (%)</label>
                        <input
                          type="number" step="1" min="0" max="100"
                          value={form.revenue_dependency_pct}
                          onChange={e => setForm(prev => ({ ...prev, revenue_dependency_pct: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          placeholder="25"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Owner */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Owner</label>
                    <input
                      type="text"
                      value={form.owner}
                      onChange={e => setForm(prev => ({ ...prev, owner: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="e.g., IT Director, Finance Dept"
                    />
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                    <textarea
                      value={form.notes}
                      onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                      style={{ minHeight: '60px' }}
                    />
                  </div>

                  {/* Footer */}
                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Cancel</button>
                    <button type="submit" disabled={saving} className="btn-primary">
                      {saving ? 'Saving...' : editingSystem ? 'Save Changes' : 'Add Asset'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Delete Confirmation */}
          {showDeleteConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-sm">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Remove System</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  Are you sure you want to remove <span className="font-medium">&ldquo;{showDeleteConfirm.name}&rdquo;</span>?
                </p>
                <div className="flex justify-end gap-3">
                  <button onClick={() => setShowDeleteConfirm(null)} className="btn-secondary">Cancel</button>
                  <button
                    onClick={() => handleDelete(showDeleteConfirm.id)}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Security Stack — CDM heatmap + tools */
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-indigo-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Cyber Defense Matrix</span>
            </div>
            {onNavigate ? (
              <button
                onClick={() => onNavigate('tech-stack-edit')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 dark:bg-accent-500 dark:hover:bg-accent-600 rounded-lg transition-colors"
              >
                Manage Stack
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            ) : (
              <Link
                to={`/clients/${client.id}/technology-stack`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 dark:bg-accent-500 dark:hover:bg-accent-600 rounded-lg transition-colors"
              >
                Manage Stack
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
            )}
          </div>

          <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            {technologies.length} tool{technologies.length !== 1 ? 's' : ''} tracked
            {' \u00b7 '}
            {25 - Object.keys(cdmGrid).length} gap{25 - Object.keys(cdmGrid).length !== 1 ? 's' : ''}
          </div>

          {/* 5x5 Mini Heatmap */}
          <div className="grid grid-cols-6 gap-1 text-xs">
            <div></div>
            {cdmData.operationalFunctions.map(func => (
              <div key={func.id} className="text-center text-gray-500 dark:text-gray-400 font-medium text-xs px-1">
                {func.name}
              </div>
            ))}

            {cdmData.assetClasses.map(asset => {
              const miniCellColors = {
                blue: { filled: 'bg-blue-200 dark:bg-blue-800', empty: 'bg-blue-50 dark:bg-blue-950 border border-dashed border-blue-200 dark:border-blue-800' },
                green: { filled: 'bg-green-200 dark:bg-green-800', empty: 'bg-green-50 dark:bg-green-950 border border-dashed border-green-200 dark:border-green-800' },
                yellow: { filled: 'bg-yellow-200 dark:bg-yellow-800', empty: 'bg-yellow-50 dark:bg-yellow-950 border border-dashed border-yellow-200 dark:border-yellow-800' },
                orange: { filled: 'bg-orange-200 dark:bg-orange-800', empty: 'bg-orange-50 dark:bg-orange-950 border border-dashed border-orange-200 dark:border-orange-800' },
                purple: { filled: 'bg-purple-200 dark:bg-purple-800', empty: 'bg-purple-50 dark:bg-purple-950 border border-dashed border-purple-200 dark:border-purple-800' }
              }
              return (
                <div key={asset.id} className="contents">
                  <div className="text-gray-500 dark:text-gray-400 font-medium text-xs pr-2 flex items-center">
                    {asset.name}
                  </div>
                  {cdmData.operationalFunctions.map(func => {
                    const key = `${asset.id}-${func.id}`
                    const tools = cdmGrid[key] || []
                    const hasTools = tools.length > 0
                    const colors = miniCellColors[func.color] || miniCellColors.blue
                    return (
                      <div
                        key={key}
                        className={`h-6 rounded-sm flex items-center justify-center transition-colors ${hasTools ? colors.filled : colors.empty}`}
                        title={`${asset.name} \u00d7 ${func.name}: ${tools.length} tool${tools.length !== 1 ? 's' : ''}`}
                      >
                        {hasTools && (
                          <span className="text-xs font-medium text-gray-700 dark:text-gray-200">{tools.length}</span>
                        )}
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>

          {/* Tool List */}
          {technologies.length > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-700 mt-4 pt-3">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Security Tools</p>
              <div className="flex flex-wrap gap-1.5">
                {technologies.slice(0, 8).map(tech => (
                  <span
                    key={tech.id}
                    className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                    title={[tech.vendor, tech.category].filter(Boolean).join(' · ')}
                  >
                    {tech.vendor && (
                      <span className="text-gray-400 dark:text-gray-500 mr-1">{tech.vendor}</span>
                    )}
                    {tech.name}
                  </span>
                ))}
                {technologies.length > 8 && onNavigate && (
                  <button
                    onClick={() => onNavigate('tech-stack')}
                    className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
                  >
                    +{technologies.length - 8} more
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* CDM Attribution */}
      {section === 'techstack' && (
        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-6">
          Security Stack Framework: <a href="https://cyberdefensematrix.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600 dark:hover:text-gray-300">Cyber Defense Matrix</a> by Sounil Yu, used under <a href="https://creativecommons.org/licenses/by-sa/3.0/" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600 dark:hover:text-gray-300">CC BY-SA 3.0</a>
        </p>
      )}
    </div>
  )
}

export default SecurityPostureTab
